import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { IconButton, Menu, MenuItem } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";

import { faCheck, faPen, faBed } from "@fortawesome/pro-solid-svg-icons";
import { faHandshakeAlt } from "@fortawesome/pro-light-svg-icons";

import type { ILabel, IPartner, IProfile } from "App/Modules/Models";
import { Permission } from "App/Modules/Models";
import { hasUserPermissionAnywhere } from "App/ReactComponents/Shared";

import { PartnerDetailedInformation } from "./PartnerDetailedInformation";
import { SupportingPartners } from "./SupportingPartners";

type PartnerIntroProps = {
    partner: IPartner;
    profile: IProfile;
    hasLimitedPermissions: boolean;
    labels: ILabel[];
    isLoading: boolean;
    isLabelsLoading: boolean;
    isLocaleAndTimeLoading: boolean;
    isDataPolicyLoading: boolean;
    chosenLabel: (label: ILabel) => void;
    editMindcareSettings: (event: any) => void;
    editPartnerDetails: (event: any) => void;
    isSupportingPartnersDialogOpen: (value: boolean) => void;
    isLocaleAndTimeDialogOpen: (value: boolean) => void;
    isEditLabelsDialogOpen: (value: boolean) => void;
    isDataPolicyDialogOpen: (value: boolean) => void;
};

export const PartnerIntro = (props: PartnerIntroProps) => {
    const {
        partner,
        profile,
        hasLimitedPermissions,
        labels,
        isLoading,
        isLabelsLoading,
        isLocaleAndTimeLoading,
        isDataPolicyLoading,
        chosenLabel,
        editMindcareSettings,
        editPartnerDetails,
        isSupportingPartnersDialogOpen,
        isLocaleAndTimeDialogOpen,
        isEditLabelsDialogOpen,
        isDataPolicyDialogOpen,
    } = props;
    const { t } = useTranslation();

    const [isEditDetailsMenuOpen, setIsEditDetailsMenuOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<Element>();

    return isLoading ? (
        <React.Fragment>
            <div className="partner-details__title">
                <div>
                    <Skeleton
                        variant="circle"
                        animation="wave"
                        height="1em"
                        width="1em"
                        className="partner-details__title-icon"
                    />
                </div>
                <div className="title title--h1">
                    <Skeleton variant="text" animation="wave" height="1.6em" width="55%" />
                </div>
            </div>
            <div className="card">
                <Skeleton
                    className="partner-details__logo"
                    variant="rect"
                    animation="wave"
                    height="70px"
                    width="100%"
                />
                <h2 className="title title--h2">
                    <Skeleton variant="text" animation="wave" height="1.6em" width="225px" />
                </h2>
                <div className="data-panels data-panels--space-between">
                    <div className="data-panels__panel">
                        <div className="caption">
                            <Skeleton variant="text" animation="wave" height="1.6em" width="100px" />
                        </div>
                        <div className="paragraph paragraph--bold">
                            <Skeleton variant="text" animation="wave" height="1.6em" width="200px" />
                        </div>
                    </div>
                </div>
                <div className="data-list data-list--extra-spacing">
                    <div className="data-list__row">
                        <div className="caption">
                            <Skeleton variant="text" animation="wave" height="1.6em" width="100px" />
                        </div>
                        <div className="data-list__line" />
                        <div className="paragraph paragraph--small paragraph--bold data-list__value">
                            <Skeleton variant="text" animation="wave" height="4em" width="200px" />
                        </div>
                    </div>
                </div>
                <div className="card card--alt card--nested card--less-padding">
                    <div className="title title--h3">
                        <Skeleton variant="text" animation="wave" height="1.8em" width="50%" />
                    </div>
                    <Skeleton variant="rect" animation="wave" height="130px" width="100%" />
                </div>
                <PartnerDetailedInformation
                    partner={partner}
                    profile={profile}
                    hasLimitedPermissions={hasLimitedPermissions}
                    labels={labels}
                    isLoading={isLoading}
                    isLabelsLoading={isLabelsLoading}
                    isLocaleAndTimeLoading={isLocaleAndTimeLoading}
                    isDataPolicyLoading={isDataPolicyLoading}
                    chosenLabel={chosenLabel}
                    isLocaleAndTimeDialogOpen={isLocaleAndTimeDialogOpen}
                    isEditLabelsDialogOpen={isEditLabelsDialogOpen}
                    isDataPolicyDialogOpen={isDataPolicyDialogOpen}
                />
            </div>
        </React.Fragment>
    ) : (
        <React.Fragment>
            <div className="partner-details__title">
                <div>
                    <FontAwesomeIcon icon={faHandshakeAlt} fixedWidth className="partner-details__title-icon" />
                </div>
                <h1 className="title title--h1">{partner.name}</h1>
            </div>
            <div className="card">
                {partner.logo ? (
                    <img
                        key={partner.logo.url}
                        className="partner-details__logo"
                        src={partner.logo.url}
                        alt="Partner Logo"
                    />
                ) : null}
                <div className="partner-details__header-with-edit">
                    <h2 className="title title--h2">{t("Entities.Partner.PartnerDetails")}</h2>
                    {!hasLimitedPermissions ? (
                        <React.Fragment>
                            <Menu
                                open={isEditDetailsMenuOpen}
                                anchorEl={menuAnchor}
                                onClose={() => setIsEditDetailsMenuOpen(false)}
                            >
                                <MenuItem
                                    onClick={(event) => {
                                        if (!hasUserPermissionAnywhere(profile, Permission.PartnerSettings)) {
                                            return;
                                        }
                                        editPartnerDetails(event);
                                        setIsEditDetailsMenuOpen(false);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPen} className="partner-details__menu-icon" />
                                    <span>{t("Entities.Partner.EditPartnerDetails")}</span>
                                </MenuItem>
                                <MenuItem
                                    onClick={(event) => {
                                        if (!hasUserPermissionAnywhere(profile, Permission.PartnerSettings)) {
                                            return;
                                        }
                                        editMindcareSettings(event);
                                        setIsEditDetailsMenuOpen(false);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faBed} className="partner-details__menu-icon" />
                                    <span>{t("Entities.Partner.EditMindcareSettings")}</span>
                                </MenuItem>
                            </Menu>
                            <IconButton
                                className="icon-button"
                                onClick={(e) => {
                                    setMenuAnchor(e.currentTarget as HTMLElement);
                                    setIsEditDetailsMenuOpen(true);
                                }}
                            >
                                <FontAwesomeIcon className="icon-button__icon" icon={faPen} />
                            </IconButton>
                        </React.Fragment>
                    ) : (
                        <IconButton
                            className="icon-button"
                            disabled={!hasUserPermissionAnywhere(profile, Permission.PartnerSettings)}
                            onClick={editPartnerDetails}
                        >
                            <FontAwesomeIcon className="icon-button__icon" icon={faPen} />
                        </IconButton>
                    )}
                </div>
                <div className="data-panels data-panels--space-between">
                    <div className="data-panels__panel">
                        <p className="caption">{t("Entities.Partner.VatId")}</p>
                        <p className="paragraph paragraph--bold">{partner.vatId ?? t("Entities.Partner.NoVatId")}</p>
                    </div>
                    <div className="data-panels__panel data-panels__panel--right-align">
                        <div className="data-panels__heading">
                            <p className="caption">{t("Entities.Common.CorporateIdNumber")}</p>
                        </div>
                        <div className="data-panels__value">
                            <p className="paragraph paragraph--bold">
                                {partner.corporateIdNumber ?? t("Entities.Common.NoCorporateIdNumber")}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="data-list data-list--extra-spacing">
                    <div className="data-list__row">
                        <div className="data-list__key">
                            <p className="caption">{t("Gui.Manage.Partner.TemplatesForEmails")}</p>
                        </div>
                        <div className="data-list__line" />
                        <div className="data-list__value">
                            <p className="paragraph paragraph--small paragraph--bold paragraph--tiny-spacing">
                                {partner.supportEmail ? partner.supportEmail : t("Gui.Manage.Partner.NoSupportEmail")}
                            </p>
                            <p className="paragraph paragraph--small paragraph--bold">
                                {partner.supportName ? partner.supportName : t("Gui.Manage.Partner.NoSupportName")}
                            </p>
                        </div>
                    </div>
                    {hasUserPermissionAnywhere(profile, Permission.GlobalSysAdmin) ? (
                        <React.Fragment>
                            <div className="data-list__row">
                                <div className="data-list__key">
                                    <p className="caption">{t("Gui.Manage.Partner.IrisCortex")}</p>
                                </div>
                                <div className="data-list__line" />
                                <div className="data-list__value">
                                    {partner.useIrisCortex ? (
                                        <p className="paragraph paragraph--small paragraph--bold">
                                            <FontAwesomeIcon
                                                className="data-list__value-icon"
                                                icon={faCheck}
                                                fixedWidth
                                            />
                                            {t("Entities.Partner.CortexConnected")}
                                        </p>
                                    ) : (
                                        <p className="paragraph paragraph--small paragraph--bold">
                                            {t("Entities.Partner.CortexNotConnected")}
                                        </p>
                                    )}
                                </div>
                            </div>
                            {partner.useIrisCortex ? (
                                <div className="data-list__row">
                                    <div className="data-list__key">
                                        <p className="caption">{t("Entities.Partner.DefaultCortexArc")}</p>
                                    </div>
                                    <div className="data-list__line" />
                                    <div className="data-list__value">
                                        <p className="paragraph paragraph--small paragraph--bold">
                                            {partner.defaultCortexArc ? partner.defaultCortexArc.name : ""}
                                        </p>
                                    </div>
                                </div>
                            ) : null}
                            {!hasLimitedPermissions ? (
                                <React.Fragment>
                                    <div className="data-list__row">
                                        <div className="data-list__key">
                                            <p className="caption">{t("Entities.Partner.SupervisionTimeWindow")}</p>
                                        </div>
                                        <div className="data-list__line" />
                                        <div className="data-list__value">
                                            <p className="paragraph paragraph--small paragraph--bold">
                                                {t("Entities.Partner.SupervisionTimeWindowMinutesCount", {
                                                    count: partner.supervisionTimeWindowMinutes,
                                                    postProcess: "interval",
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="data-list__row">
                                        <div className="data-list__key">
                                            <p className="caption">{t("Entities.Partner.CameraMaxMinutes")}</p>
                                        </div>
                                        <div className="data-list__line" />
                                        <div className="data-list__value">
                                            <p className="paragraph paragraph--small paragraph--bold">
                                                {partner.cameraMaxMinutes
                                                    ? t("Entities.Partner.CameraMaxMinutesCount", {
                                                          count: partner.cameraMaxMinutes,
                                                          postProcess: "interval",
                                                      })
                                                    : t("Entities.Partner.CameraMaxMinutesUnlimited")}
                                            </p>
                                        </div>
                                    </div>
                                </React.Fragment>
                            ) : null}
                        </React.Fragment>
                    ) : null}
                </div>
                <SupportingPartners
                    isLoading={isLoading}
                    partner={partner}
                    profile={profile}
                    isSupportingPartnersDialogOpen={isSupportingPartnersDialogOpen}
                />
                <PartnerDetailedInformation
                    partner={partner}
                    profile={profile}
                    hasLimitedPermissions={hasLimitedPermissions}
                    labels={labels}
                    isLoading={isLoading}
                    isLabelsLoading={isLabelsLoading}
                    isLocaleAndTimeLoading={isLocaleAndTimeLoading}
                    isDataPolicyLoading={isDataPolicyLoading}
                    chosenLabel={chosenLabel}
                    isLocaleAndTimeDialogOpen={isLocaleAndTimeDialogOpen}
                    isEditLabelsDialogOpen={isEditLabelsDialogOpen}
                    isDataPolicyDialogOpen={isDataPolicyDialogOpen}
                />
            </div>
        </React.Fragment>
    );
};
